import { isObject } from "@entrilia/shared/utilities/objectHelper.ts";
import { AmountType } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ConditionField,
  DimensionDescriptor,
  MeasureDataset,
} from "../../../../../../../../../api/types/svTypes.ts";
import { MeasureUnitTable } from "../../../../../../../../../api/types/svTypes.ts";
import { checkUnitTable } from "../../../../../utilities/checkUnitTable.ts";
import { sanitizeFieldName } from "../../utilities/sanitizeFieldName.ts";
import type { CustomMeasureState, NodesCounter } from "../types/customMeasure.types";
import type { Actions } from "../types/customMeasureStateActions";
import { CustomMeasureActionType } from "../types/customMeasureStateActions";
import type { FormulaNode } from "../utilities/fillNodes";
import { fillNodes } from "../utilities/fillNodes";
import { findNodeByTokenData } from "../utilities/findNodeByTokenData";
import type { INode } from "../utilities/formulaParser";
import { NodeFunction, NodeNumber, NodeVariable } from "../utilities/formulaParser";

export const CustomMeasureReducer = (state: CustomMeasureState, action: Actions): CustomMeasureState => {
  switch (action.type) {
    case CustomMeasureActionType.FORMULA_PARAMETER_CLICK: {
      return { ...state, selectedFormulaParameter: action.payload };
    }
    case CustomMeasureActionType.FUNCTION_HOVERED: {
      return { ...state, functionHovered: action.payload };
    }
    case CustomMeasureActionType.HIGHLIGHT_NODE: {
      if (state.formulaNodes.length === 0) return state;
      const newState = cloneDeep(state);
      const formulaNodes: FormulaNode[] = [];
      newState.formulaNodes.forEach((fn) => {
        let node: FormulaNode | undefined;
        const highlight = fn.linkedNodes.some((ln) => findNodeByTokenData(ln, action.payload));
        node = { ...fn, highlighted: highlight };
        if (node.args !== undefined) {
          const argsFormulaNodes: FormulaNode[] = [];
          node.args.forEach((arg) => {
            const highlight = arg.linkedNodes.some((ln) => findNodeByTokenData(ln, action.payload));
            argsFormulaNodes.push({ ...arg, highlighted: highlight });
          });
          node = { ...node, args: argsFormulaNodes };
        }
        formulaNodes.push(node);
      });
      return { ...newState, formulaNodes: formulaNodes };
    }
    case CustomMeasureActionType.UPDATE: {
      return { ...state, ...action.payload };
    }
    case CustomMeasureActionType.UPDATE_CAPTION: {
      const newState = { ...state, caption: sanitizeFieldName(action.payload) };
      return newState;
    }
    case CustomMeasureActionType.SET_ROOTNODE: {
      const newState = { ...cloneDeep(state), rootNode: action.payload };
      const nodesCounter: NodesCounter = {
        nodeVariable: 0,
        nodeFunction: 0,
        nodeNumber: 0,
      };
      newState.formulaNodes = [];

      if (action.payload === undefined) {
        newState.currentToken = undefined;
      } else {
        const increaseNodesCounter = (node: INode) => {
          if (node instanceof NodeVariable) {
            nodesCounter.nodeVariable++;
          } else if (node instanceof NodeFunction) {
            nodesCounter.nodeFunction++;
          } else if (node instanceof NodeNumber) {
            nodesCounter.nodeNumber++;
          }
        };
        fillNodes(newState.formulaNodes, state.formulaNodes, action.payload, newState.functions, increaseNodesCounter);
      }
      newState.nodesCounter = newState.formula ? nodesCounter : undefined;

      return newState;
    }
    case CustomMeasureActionType.UPDATE_FORMULA_NODE: {
      const newState = { ...cloneDeep(state) };
      newState.formulaNodes.forEach((fn) => {
        if (fn.function !== undefined && fn.args !== undefined) {
          fn.args.forEach((arg) => {
            if (arg.key === action.payload.node.key) {
              Object.assign(arg, action.payload.changes);
            }
          });
        }
        if (fn.key === action.payload.node.key) {
          Object.assign(fn, action.payload.changes);
        }
      });
      return newState;
    }
    case CustomMeasureActionType.UPDATE_DIMENSIONS: {
      const newState: CustomMeasureState = { ...cloneDeep(state), dimensions: action.payload.dimensions };
      newState.formulaNodes.forEach((fn) => {
        updateDictionaryValues(fn, newState.dimensions);
        updateUnitTableAndAmountType(fn, newState);
      });

      return newState;
    }
    case CustomMeasureActionType.ADD_CONDITIONS: {
      const newState: CustomMeasureState = cloneDeep(state);
      const node = getNode(newState.formulaNodes, action.payload.node);
      if (node !== undefined) {
        node.conditions = (node.conditions || []).concat(action.payload.newConditions);
        updateUnitTableAndAmountType(node, newState);
      }
      return newState;
    }
    case CustomMeasureActionType.UPDATE_CONDITION: {
      const newState: CustomMeasureState = cloneDeep(state);
      const node = getNode(newState.formulaNodes, action.payload.node);
      if (node?.conditions !== undefined) {
        const condition = node.conditions.find((c) => c.meta.name === action.payload.condition.meta.name);
        if (condition !== undefined) {
          Object.assign(condition, action.payload.changes);
        }
        updateUnitTableAndAmountType(node, newState);
      }
      return newState;
    }
    case CustomMeasureActionType.REMOVE_CONDITION: {
      const newState: CustomMeasureState = cloneDeep(state);
      const node = getNode(newState.formulaNodes, action.payload.node);
      if (node?.conditions !== undefined) {
        const index = node.conditions.findIndex((c) => c.meta.name === action.payload.condition.meta.name);
        if (index > -1) {
          node.conditions.splice(index, 1);
          node.conditions = [...node.conditions];
        }
      }
      return newState;
    }
    case CustomMeasureActionType.SET_DATASETS: {
      const newState: CustomMeasureState = cloneDeep(state);
      newState.dataSets = action.payload;

      return newState;
    }
    default:
      return state;
  }
};

export const getNode = (nodes: FormulaNode[], actualNode: FormulaNode) => {
  let node: FormulaNode | undefined;
  const findNode = (fnds: FormulaNode[]) => {
    fnds.forEach((fn) => {
      if (node !== undefined) return;
      if (fn.key === actualNode.key) {
        node = fn;
      } else if (fn.args !== undefined) {
        findNode(fn.args);
      }
    });
  };
  findNode(nodes);
  return node;
};

function updateUnitTableAndAmountType(fn: FormulaNode, state: CustomMeasureState) {
  if (fn.conditions === undefined || fn.conditions.length === 0) {
    fn.table = MeasureUnitTable.Gl;
    return;
  }
  const glDataSet = state.dataSets.find((ds) => ds.table === MeasureUnitTable.Gl);
  if (glDataSet === undefined) {
    fn.table = MeasureUnitTable.Gl;
    return;
  }
  const memoDataSet = state.dataSets.find((ds) => ds.table === MeasureUnitTable.Memo);
  if (memoDataSet === undefined) {
    fn.table = MeasureUnitTable.Gl;
    return;
  }

  const glValues = hasDataSetValues(fn.conditions, glDataSet);
  const memoValues = hasDataSetValues(fn.conditions, memoDataSet);
  if (glValues && memoValues) {
    fn.table = MeasureUnitTable.Gl | MeasureUnitTable.Memo;
  } else if (glValues) {
    fn.table = MeasureUnitTable.Gl;
  } else if (memoValues) {
    fn.table = MeasureUnitTable.Memo;
  } else {
    fn.table = MeasureUnitTable.Gl;
  }

  if (checkUnitTable(fn.table, MeasureUnitTable.Memo)) {
    resetAmountTypeToDefault(state);
  }
}

function hasDataSetValues(conditions: ConditionField[], dataset: MeasureDataset) {
  return conditions.some((c) => {
    if (c.config.filter.dimensionName === undefined) return false;
    const values = dataset.values[c.meta.name];
    if (values === undefined) return false;
    return c.config.filter.values.some((v) => values.includes(v ?? ""));
  });
}

const updateDictionaryValues = (fn: FormulaNode, dimensions: DimensionDescriptor[]) => {
  if (fn.function === undefined && fn.conditions !== undefined && fn.conditions.length > 0) {
    fn.conditions.forEach((condition) => {
      const filter = condition.config.filter;
      if (filter === undefined) return;
      const dimension = dimensions.find((d) => d.name === filter.dimensionName);
      if (dimension !== undefined && dimension.dictionary.length > 0) {
        condition.meta.dictionary = dimension.dictionary;
      }
    });
  }
  if (fn.function !== undefined && fn.args !== undefined) {
    fn.args.forEach((node) => updateDictionaryValues(node, dimensions));
  }
};

const resetAmountTypeToDefault = (state: CustomMeasureState) => {
  state.amountType = AmountType.Net;
};

function cloneDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((element: unknown) => cloneDeep(element)) as T;
  }

  if (isObject(value)) {
    const result: Partial<T> = {};
    for (const key in value) {
      result[key] = cloneDeep(value[key]);
    }
    return result as T;
  }

  return value;
}
